import RainfallChart from '../RainfallChart.js';

import { Row, Col, Table } from 'react-bootstrap';
import { FMRainfallStation } from '../FloodMapperIcons';

import CommonHeaders from '../helmet-headers/CommonHeaders.js';

import NumberDisplay from '../NumberDisplay.js';


import { formatTimestamp } from '../../lib/formatHelpers.js';

import RenderMonthlyRainfallChart from '../RenderMonthlyRainfallChart.js';
import RenderRainfallCommentary from '../RenderRainfallCommentary.js';

/**
 * key={"includedStationPart_rf_" + station.stationId}
						isMissingMetrics={isMissingMetrics}
						station={station}
						stationName={stationName}
						computedQualifierType={computedQualifierType}
						qualifierType={props.qualifierType}*/
function RenderStationRainfall({ station, isMissingMetrics, stationName, computedQualifierType, qualifierType }) {

	if (!(station && station.rainfallHistory && Array.isArray(station.rainfallHistory))) {
	  return <span>An error occurred presenting the data for this station</span>;
	}

	let minByMonth = [], maxByMonth = [], meanByMonth = [];
	if(station && station.metadata) {
		minByMonth = [
			station.metadata.minJan,
			station.metadata.minFeb,
			station.metadata.minMar,
			station.metadata.minApr,
			station.metadata.minMay,
			station.metadata.minJun,
			station.metadata.minJul,
			station.metadata.minAug,
			station.metadata.minSep,
			station.metadata.minOct,
			station.metadata.minNov,
			station.metadata.minDec,
		];
		meanByMonth = [
			station.metadata.meanJan,
			station.metadata.meanFeb,
			station.metadata.meanMar,
			station.metadata.meanApr,
			station.metadata.meanMay,
			station.metadata.meanJun,
			station.metadata.meanJul,
			station.metadata.meanAug,
			station.metadata.meanSep,
			station.metadata.meanOct,
			station.metadata.meanNov,
			station.metadata.meanDec,
		];
		maxByMonth = [
			station.metadata.maxJan,
			station.metadata.maxFeb,
			station.metadata.maxMar,
			station.metadata.maxApr,
			station.metadata.maxMay,
			station.metadata.maxJun,
			station.metadata.maxJul,
			station.metadata.maxAug,
			station.metadata.maxSep,
			station.metadata.maxOct,
			station.metadata.maxNov,
			station.metadata.maxDec,
		];
	}

	let rainfallDataRev = [...station.rainfallHistory].reverse(); // Copy and reverse to avoid mutating original data
	let mostRecent = rainfallDataRev.find(entry => entry.entryTotal !== null);

	// If mostRecent is null, return an error message
	if (!mostRecent) {
	  return <span>No valid rainfall data available</span>;
	}

	// Convert entryTimestamp to a moment object for calculations
	const mostRecentTime = new Date(mostRecent.entryTimestamp);

	// Helper function to sum the rainfall totals in a given time range
	const calculateTotalForPeriod = (hoursBack) => {
	  const startTime = new Date(mostRecentTime);
	  startTime.setHours(mostRecentTime.getHours() - hoursBack);
	  return rainfallDataRev
	    .filter(entry => {
	      const entryTime = new Date(entry.entryTimestamp);
	      return entryTime >= startTime && entryTime <= mostRecentTime;
	    })
	    .reduce((sum, entry) => sum + (entry.entryTotal !== null ? parseFloat(entry.entryTotal) : 0), 0);
	};

	// Calculate sums for the periods
	const oneHourTotal = calculateTotalForPeriod(1);
	const threeHoursTotal = calculateTotalForPeriod(3);
	const twelveHoursTotal = calculateTotalForPeriod(12);
	const twentyFourHoursTotal = calculateTotalForPeriod(24);

	const hasForecast = station && !!station.rainfallForecast;
	const hasHistory = station && station.metadata && station.metadata.minJun;
	const calculateForecastForPeriod = (hoursForward) => {
	  if(station.rainfallForecast && Array.isArray(station.rainfallForecast) && station.rainfallForecast.length >= hoursForward) {
	  	const sum = station.rainfallForecast
	      .slice(0, hoursForward) // only take the next N hours
	      .reduce((acc, hour) => acc + (Number(hour.precipitation) || 0), 0);

	    return sum;
	  } else {
	  	return "N/A";
	  }
	}

	let oneHourForecast = null;
	let threeHoursForecast = null;
	let twelveHoursForecast = null;
	let twentyFourHoursForecast = null;
	let forecastElement = null;
	if(hasForecast && Array.isArray(station.rainfallForecast) && station.rainfallForecast.length >= 48) {
		forecastElement = <Table striped bordered hover>
	            <thead>
	              <tr>
	                <th>Forecast Period</th>
	                <th>Total* (mm)</th>
	                {hasHistory ? <th>Commentary**</th> : null}
	              </tr>
	            </thead>
	            <tbody>
	              <tr>
	                <td>{formatTimestamp(station.rainfallForecast[0].time)}</td>
	                <td><NumberDisplay value={calculateForecastForPeriod(1)} /></td>
	            	{hasHistory ? <td><span style={{fontSize: "0.7em"}}><i>N/A</i></span></td> : null}
	              </tr>
	              <tr>
	                <td>+3 hour</td>
	                <td><NumberDisplay value={calculateForecastForPeriod(3)} /></td>
	            	{hasHistory ? <td><span style={{fontSize: "0.7em"}}><i>N/A</i></span></td> : null}
	              </tr>
	              <tr>
	                <td>+12 hours</td>
	                <td><NumberDisplay value={calculateForecastForPeriod(12)} /></td>
	            	{hasHistory ? <td><RenderRainfallCommentary periodTotal={calculateForecastForPeriod(12)} meanByMonth={meanByMonth} /></td> : null}
	              </tr>
	              <tr>
	                <td>+24 hours</td>
	                <td><NumberDisplay value={calculateForecastForPeriod(24)} /></td>
	            	{hasHistory ? <td><RenderRainfallCommentary periodTotal={calculateForecastForPeriod(24)} meanByMonth={meanByMonth} /></td> : null}
	              </tr>
	              <tr>
	                <td>+48 hours</td>
	                <td><NumberDisplay value={calculateForecastForPeriod(48)} /></td>
	            	{hasHistory ? <td><RenderRainfallCommentary periodTotal={calculateForecastForPeriod(48)} meanByMonth={meanByMonth} /></td> : null}
	              </tr>
	            </tbody>
	    </Table>;
	}


	let metaTitle = "The recent rainfall record at the " + stationName + " station";
	if(station.placeName) {
		metaTitle += " in " + station.placeName;
	}
	metaTitle += ".";

	let metaDescription = "Check the latest rainfall data at the " + stationName + " measuring station" + (station.placeName ? " in " + station.placeName : "") + ". View local data and stay informed with real-time updates and alerts.";


	let rainfallHistoricalChart = null;
	if(hasHistory) {
		rainfallHistoricalChart = [
			<Col key="sn_h_mrainfall" xs={12} className="header-bold bg-secondary text-white"><h3 className="hX-blendin">Historical Monthly Rainfall Chart</h3></Col>,
		]
		if(station.metadata.avgHistoryStartYear && station.metadata.avgHistoryEndYear) {
			rainfallHistoricalChart.push(
				<Col key="sn_d_mrainfall" xs={12} style={{fontSize: "0.7em"}}>
					<i><b>Calculation Period</b>: {station.metadata.avgHistoryStartYear}-{station.metadata.avgHistoryEndYear}</i>
				</Col>
			);
		}
		rainfallHistoricalChart.push(
			<Col key="sn_b_mrainfall" xs={12}>
		    	<RenderMonthlyRainfallChart minByMonth={minByMonth} maxByMonth={maxByMonth} meanByMonth={meanByMonth} />
		    </Col>
		);
	}

	return (
	  <Row>
	  	<CommonHeaders
			key={"helmet_flow_h_"+station.metricId}
			title={metaTitle}
			description={metaDescription}
			/*Canonical handled by station handler*/
			/*townName handled by station handler*/
			/*ogType handled by station handler*/
			/*lat/lng handled by station handler*/
		/>
	    <Col xs={12} md={4}>
	      <Row>
	        <Col xs={12} style={{ fontSize: "2.8em" }} className="d-none d-md-block">
	          <FMRainfallStation />
	        </Col>
	        <Col xs={12} style={{ fontSize: "1.2em" }}><b>{stationName}</b></Col>
	        <Col xs={12}>
	          <Table striped bordered hover>
	            <thead>
	              <tr>
	                <th>Period</th>
	                <th>Total (mm)</th>
	              </tr>
	            </thead>
	            <tbody>
	              <tr>
	                <td>{formatTimestamp(mostRecent.entryTimestamp)}</td>
	                <td><NumberDisplay value={mostRecent.entryTotal} /></td>
	              </tr>
	              <tr>
	                <td>-1 hour</td>
	                <td><NumberDisplay value={oneHourTotal} /></td>
	              </tr>
	              <tr>
	                <td>-3 hours</td>
	                <td><NumberDisplay value={threeHoursTotal} /></td>
	              </tr>
	              <tr>
	                <td>-12 hours</td>
	                <td><NumberDisplay value={twelveHoursTotal} /></td>
	              </tr>
	              <tr>
	                <td>-24 hours</td>
	                <td><NumberDisplay value={twentyFourHoursTotal} /></td>
	              </tr>
	            </tbody>
	          </Table>
	        </Col>
	        <Col xs={12} style={{ fontSize: "0.7em" }}>
	        	Recent period data caclulated from: {formatTimestamp(mostRecent.entryTimestamp)}<br />
	        	<span>(e.g. "-1 hour" is the total in the hour leading up to {formatTimestamp(mostRecent.entryTimestamp)})</span>
	        </Col>
	      </Row>
	    </Col>
	    <Col xs={12} md={8}>
	      {station && station.rainfallHistory && Array.isArray(station.rainfallHistory) ? (
	        <RainfallChart data={station.rainfallHistory} />
	      ) : "No history data to graph"}
	    </Col>
	    {(forecastElement || rainfallHistoricalChart) ? <Col xs={12} className="bg-dark text-white header-bold">Rainfall in Context</Col> : null}
	    {forecastElement ? <Col xs={12} className="bg-secondary text-white header-bold">Upcoming Forecast</Col> : null}
	    {forecastElement ? <Col xs={12}>
        	{forecastElement}
        </Col> : null}
        {forecastElement ? <Col xs={12} style={{ fontSize: "0.7em" }}>
        	<span>
			  <i>
			    *Rainfall totals are based on the GFS 0.125° global model (NOAA/NCEP).  
			    This data is <b>model guidance</b> and <b>not an official forecast</b>.  
			    Model skill is limited for localised heavy rainfall, especially at short
			    timescales (12–48h).  
			    For official weather forecasts and warnings, please consult your national
			    meteorological service.<br />
			    {hasHistory ? <span>**Commentary is provided by FloodMapper using monthly historical data for this
			    station, and is intended as <b>guidance only</b></span> : null}.
			  </i>
			</span>

        </Col> : null}
	    {rainfallHistoricalChart}
	  </Row>
	);

}


export default RenderStationRainfall;